/* @src/pre-deployment/src/components/ConsoleOverlay.js */
import React, { useState, useEffect, useRef } from 'react';
import '../../styles/ConsoleOverlay.css';

const ConsoleOverlay = ({ title = 'System Log', type = 'all', isVisible, onClose }) => {
    const [logs, setLogs] = useState([]);
    const logsEndRef = useRef(null);

    // Auto-scroll to bottom
    const scrollToBottom = () => {
        logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [logs]);

    useEffect(() => {
        const handleLogEvent = (event) => {
            const { level, message, timestamp, category } = event.detail;

            // Filter by type if needed
            if (type !== 'all' && category !== type) return;

            setLogs(prev => [...prev, { level, message, timestamp, category }].slice(-100));
        };

        window.addEventListener('app-log', handleLogEvent);
        return () => window.removeEventListener('app-log', handleLogEvent);
    }, [type]);

    if (!isVisible) return null;

    return (
        <div className="console-overlay">
            <div className="console-header">
                <h3>{title}</h3>
                <div className="console-controls">
                    <button className="console-btn" onClick={() => setLogs([])} title="Clear">
                        <i className="fas fa-trash-alt"></i>
                    </button>
                    <button className="console-btn" onClick={onClose} title="Close">
                        <i className="fas fa-times"></i>
                    </button>
                </div>
            </div>
            <div className="console-logs">
                {logs.length === 0 ? (
                    <div style={{ padding: '20px', color: '#666', textAlign: 'center' }}>
                        No logs available for {title}
                    </div>
                ) : (
                    logs.map((log, i) => (
                        <div key={i} className={`log-entry ${log.level} ${log.category}`}>
                            <span className="log-time">[{new Date(log.timestamp).toLocaleTimeString()}]</span>
                            <span className="log-msg">
                                {typeof log.message === 'object' ? JSON.stringify(log.message) : log.message}
                            </span>
                        </div>
                    ))
                )}
                <div ref={logsEndRef} />
            </div>
            <div className="console-footer">
                <span style={{ fontSize: '0.6rem', color: '#444' }}>v1.0.0 Console Utility</span>
            </div>
        </div>
    );
};

// Global helper to push logs to the overlay
export const pushLog = (message, level = 'info', category = 'system') => {
    const event = new CustomEvent('app-log', {
        detail: {
            message,
            level,
            category,
            timestamp: Date.now()
        }
    });
    window.dispatchEvent(event);

    // Also log to actual console for devtools
    const prefix = `[${category.toUpperCase()}]`;
    if (level === 'error') console.error(prefix, message);
    else if (level === 'warn') console.warn(prefix, message);
    else console.log(prefix, message);
};

export default ConsoleOverlay;
